import type { AxiosInstance } from "axios";
import { Loader2, UserPlus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { Select } from "../components/ui/select.js";
import { Switch } from "../components/ui/switch.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table.js";
import { useTableResize } from "../components/TableResizeHandle.js";
import { apiErrorMessage } from "../lib/api.js";
import {
  fetchInviteScopes,
  fetchTeam,
  inviteMember,
  seatSummary,
  seatsExhausted,
  setTrackingPermission,
  type InviteScopes,
  type Member,
  type MemberRole,
  type Team,
} from "../lib/team.js";

/**
 * The team roster and the invite form — owner/admin only (the sidebar hides it from
 * analysts, and the API refuses them regardless).
 *
 * SEATS ARE SHOWN AND ENFORCED IN TWO PLACES, on purpose. The form disables itself at the
 * limit so a merchant is told before they type, and the API re-checks on invite so a
 * second admin inviting at the same moment cannot slip past a stale count. The banner is
 * the courtesy; the server is the authority.
 *
 * INVITED vs ACTIVE is drawn as a badge. An invited person holds a seat but cannot sign in
 * until they redeem the link, and a roster that showed them the same as everyone else would
 * make "why can't they log in" unanswerable.
 *
 * TRACKING CONTROL (Plugin Control) is a column: who may switch the store's tracking off. An owner
 * always may and an analyst never may; for an admin it is a switch only an OWNER can flip.
 */
export function TeamScreen({
  client,
  viewerRole,
}: {
  readonly client: AxiosInstance;
  /** The signed-in member's role on this store — decides whether the tracking switches are editable. */
  readonly viewerRole: MemberRole;
}): React.JSX.Element {
  const [team, setTeam] = useState<Team | null>(null);
  const { colWidths, startResize } = useTableResize([200, 240, 120, 150, 110, 170]);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [savingFor, setSavingFor] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setTeam(await fetchTeam(client));
      setLoadError(null);
    } catch (caught) {
      setLoadError(apiErrorMessage(caught, "Could not load your team."));
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loadError !== null && team === null) {
    return <p className="text-destructive p-6 text-sm">{loadError}</p>;
  }
  if (team === null) {
    return (
      <p className="text-muted-foreground flex items-center gap-2 p-6 text-sm">
        <Loader2 className="size-4 animate-spin" />
        Loading your team…
      </p>
    );
  }

  return (
    <div className="mx-auto flex min-w-0 flex-col gap-4">
      <header>
        <h1 className="text-xl font-semibold">Team</h1>
        <p className="text-muted-foreground text-sm">
          Invite people to this workspace. {seatSummary(team.seatsUsed, team.seatsLimit)}.
        </p>
      </header>

      <InviteForm client={client} team={team} onInvited={() => void load()} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">People</CardTitle>
          <CardDescription>
            Everyone with access to this organization. Tracking control lets an admin switch this store's tracking
            off and on{viewerRole === "owner" ? "; only owners can grant it" : ""}.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {permissionError !== null && (
            <p className="text-destructive text-sm" role="alert">
              {permissionError}
            </p>
          )}
          <div className="overflow-x-auto">
            <Table className="table-fixed">
              <colgroup>
                {colWidths.map((w, i) => (
                  <col key={i} style={{ width: `${w}px` }} />
                ))}
              </colgroup>
              <TableHeader>
                <TableRow>
                  <TableHead onResize={(e) => startResize(0, e)}>Name</TableHead>
                  <TableHead onResize={(e) => startResize(1, e)}>Email</TableHead>
                  <TableHead onResize={(e) => startResize(2, e)}>Role</TableHead>
                  <TableHead onResize={(e) => startResize(3, e)}>Access</TableHead>
                  <TableHead onResize={(e) => startResize(4, e)}>Status</TableHead>
                  <TableHead onResize={(e) => startResize(5, e)}>Tracking control</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {team.members.map((m) => (
                  <TableRow key={m.userId}>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell className="text-muted-foreground">{m.email}</TableCell>
                    <TableCell className="capitalize">{m.role}</TableCell>
                    <TableCell className="text-muted-foreground truncate" title={m.clientName ?? undefined}>
                      {m.clientId === null ? "All stores" : (m.clientName ?? "One client")}
                    </TableCell>
                    <TableCell>
                      {m.status === "invited" ? (
                        <Badge variant="secondary">Invited</Badge>
                      ) : (
                        <Badge variant="outline">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <TrackingControlCell
                        member={m}
                        editable={viewerRole === "owner"}
                        saving={savingFor === m.userId}
                        onChange={async (enabled) => {
                          setSavingFor(m.userId);
                          setPermissionError(null);
                          try {
                            await setTrackingPermission(client, m.userId, enabled);
                            setTeam(await fetchTeam(client));
                          } catch (caught) {
                            setPermissionError(apiErrorMessage(caught, `Could not change ${m.name}'s tracking control.`));
                          } finally {
                            setSavingFor(null);
                          }
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function TrackingControlCell({
  member,
  editable,
  saving,
  onChange,
}: {
  readonly member: Member;
  readonly editable: boolean;
  readonly saving: boolean;
  readonly onChange: (enabled: boolean) => Promise<void>;
}): React.JSX.Element {
  if (member.role === "owner") return <span className="text-muted-foreground text-sm">Always</span>;
  if (member.role === "analyst") return <span className="text-muted-foreground text-sm">—</span>;
  if (!editable) {
    return <span className="text-sm">{member.canControlTracking ? "Allowed" : "Not allowed"}</span>;
  }
  return (
    <span className="inline-flex items-center gap-2">
      <Switch
        checked={member.canControlTracking}
        disabled={saving}
        aria-label={`Allow ${member.name} to switch tracking on and off`}
        onCheckedChange={(checked) => void onChange(checked)}
      />
      <span className="text-muted-foreground text-sm">{member.canControlTracking ? "Allowed" : "Not allowed"}</span>
      {saving && <Loader2 className="text-muted-foreground size-3.5 animate-spin" />}
    </span>
  );
}

const ROLES: readonly { readonly value: MemberRole; readonly label: string }[] = [
  { value: "analyst", label: "Analyst — reads reports" },
  { value: "admin", label: "Admin — reads reports and invites people" },
  { value: "owner", label: "Owner — full control" },
];

function InviteForm({
  client,
  team,
  onInvited,
}: {
  readonly client: AxiosInstance;
  readonly team: Team;
  readonly onInvited: () => void;
}): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<MemberRole>("analyst");
  const [scopes, setScopes] = useState<InviteScopes>({ orgWide: true, clients: [] });
  /** "" = the whole organization; otherwise one client's id. */
  const [access, setAccess] = useState("");

  useEffect(() => {
    void fetchInviteScopes(client)
      .then((loaded) => {
        setScopes(loaded);
        // A client-scoped inviter can only invite into their own client.
        if (!loaded.orgWide) setAccess(loaded.clients[0]?.id ?? "");
      })
      .catch((error: unknown) => {
        // Fully handled: without the list the form invites org-wide, as it always did.
        void error;
      });
  }, [client]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const full = seatsExhausted(team.seatsUsed, team.seatsLimit);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await inviteMember(client, {
        email: email.trim(),
        name: name.trim() || undefined,
        role,
        ...(access === "" ? {} : { clientId: access }),
      });
      setNotice(
        result.alreadyMember
          ? `${email.trim()} is already on the team — we re-sent their link.`
          : `Invite sent to ${email.trim()}.`,
      );
      setEmail("");
      setName("");
      onInvited();
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not send the invite."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <form onSubmit={(e) => void submit(e)}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserPlus className="text-primary size-5" />
            Invite someone
          </CardTitle>
          <CardDescription>They get an email with a link to set a password and join.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 py-4">
          {full && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              You have used all {String(team.seatsLimit)} seats on your plan. Upgrade to invite more people.
            </p>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                required
                placeholder="teammate@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={full}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="invite-name">Name (optional)</Label>
              <Input
                id="invite-name"
                placeholder="Their name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={full}
              />
            </div>
          </div>

          {/*
            AGENCY SCOPING: invite a client's people to that client's stores only. Shown when there is a
            choice (an org-wide inviter with more than one client), stated when there is not (a
            client-scoped inviter can only invite into their own client).
          */}
          {scopes.orgWide && scopes.clients.length > 1 && (
            <div className="grid gap-1.5">
              <Label htmlFor="invite-access">Access</Label>
              <Select id="invite-access" value={access} onChange={(e) => setAccess(e.target.value)} disabled={full}>
                <option value="">All stores in this organization</option>
                {scopes.clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    Only {c.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
          {!scopes.orgWide && scopes.clients[0] !== undefined && (
            <p className="text-muted-foreground text-sm">They will see {scopes.clients[0].name}'s stores only.</p>
          )}

          <div className="grid gap-1.5">
            <Label htmlFor="invite-role">Role</Label>
            <Select
              id="invite-role"
              value={role}
              onChange={(e) => setRole(e.target.value as MemberRole)}
              disabled={full}
            >
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Select>
          </div>

          {error !== null && (
            <p className="text-destructive text-sm" role="alert">
              {error}
            </p>
          )}
          {notice !== null && (
            <p className="text-sm text-emerald-700 dark:text-emerald-400" role="status">
              {notice}
            </p>
          )}

          <Button type="submit" disabled={busy || full} className="w-fit">
            {busy ? <Loader2 className="size-4 animate-spin" /> : <UserPlus className="size-4" />}
            {busy ? "Sending…" : "Send invite"}
          </Button>
        </CardContent>
      </form>
    </Card>
  );
}
